/**
 * Uploading a CSV to a managed database in chunks (DESIGN_EDITING.md console flow 9): reserve staging, then PUT each
 * 1 MiB chunk, each tried up to 3 times with its sha256 compared to the answer's. The caller commits afterwards with
 * the whole-file sha256. No imports (dataView.ts says why); the network and the digest are injected.
 */

export const IMPORT_CHUNK_BYTES = 1024 * 1024; // 1 MiB, the helper's chunk cap
export const IMPORT_MAX_BYTES = 20 * 1024 * 1024; // 20 MiB, the whole file
export const CHUNK_TRIES = 3;

/** The calls an upload makes, so a test can stand in for the network. */
export interface ImportApi {
    start: (importId: string, bytes: number, chunks: number) => Promise<{ import_id: string; chunk_bytes: number }>;
    putChunk: (importId: string, seq: number, chunk: Uint8Array) => Promise<{ seq: number; sha256: string }>;
}

export type UploadOutcome =
    | { ok: true; importId: string; sha256: string }
    | { ok: false; reason: 'too_large' | 'failed' | 'cancelled'; code?: string };

const hex = (buf: ArrayBuffer): string => Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');

/** SHA-256 of bytes as hex, via the browser's crypto.subtle; injectable for tests. */
export const sha256Hex = async (bytes: Uint8Array): Promise<string> => hex(await crypto.subtle.digest('SHA-256', bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer));

export const chunkCount = (bytes: number): number => Math.max(1, Math.ceil(bytes / IMPORT_CHUNK_BYTES));

/** A short code mapped from a rejected call, so the caller can word the failure; 'failed' otherwise. */
const codeOf = (err: any): string | undefined => {
    const c = err?.response?.data?.error?.code;
    return typeof c === 'string' ? c : undefined;
};

/**
 * Reserve staging and upload the file. `newImportId` makes the id (a uuid). `onProgress(sent, total)` reports bytes
 * uploaded. `cancelled()` is checked before each chunk so leaving the modal stops it. The caller commits with the
 * returned whole-file sha256 (never re-uploading on an unknown commit outcome).
 */
export const runImportUpload = async (
    api: ImportApi, bytes: Uint8Array, newImportId: () => string,
    onProgress: (sent: number, total: number) => void,
    cancelled: () => boolean = () => false,
    digest: (b: Uint8Array) => Promise<string> = sha256Hex,
): Promise<UploadOutcome> => {
    if (bytes.length === 0) return { ok: false, reason: 'failed' };
    if (bytes.length > IMPORT_MAX_BYTES) return { ok: false, reason: 'too_large' };
    const importId = newImportId();
    const chunks = chunkCount(bytes.length);
    try {
        await api.start(importId, bytes.length, chunks);
    } catch (err) {
        return { ok: false, reason: codeOf(err) === 'import_too_large' ? 'too_large' : 'failed', code: codeOf(err) };
    }
    onProgress(0, bytes.length);
    for (let seq = 0; seq < chunks; seq++) {
        if (cancelled()) return { ok: false, reason: 'cancelled' };
        const chunk = bytes.subarray(seq * IMPORT_CHUNK_BYTES, Math.min((seq + 1) * IMPORT_CHUNK_BYTES, bytes.length));
        const want = await digest(chunk);
        let placed = false;
        for (let attempt = 0; attempt < CHUNK_TRIES && !placed; attempt++) {
            try {
                const ans = await api.putChunk(importId, seq, chunk);
                if (ans.seq === seq && ans.sha256 === want) placed = true;
            } catch (err) {
                if (attempt === CHUNK_TRIES - 1) return { ok: false, reason: 'failed', code: codeOf(err) };
            }
        }
        if (!placed) return { ok: false, reason: 'failed', code: 'chunk_mismatch' };
        onProgress(Math.min((seq + 1) * IMPORT_CHUNK_BYTES, bytes.length), bytes.length);
    }
    return { ok: true, importId, sha256: await digest(bytes) };
};

export const uploadProgress = (sent: number, total: number): string =>
    `Uploading ${total === 0 ? 0 : Math.round((sent / total) * 100)}%`;

/** A refusal of the upload itself, worded (DESIGN_EDITING.md "Error lines"); anything else is "Upload failed.". */
const UPLOAD_BY_CODE: Readonly<Record<string, string>> = {
    import_busy: 'Another import is running.',
    disk_low: 'Disk almost full.',
    rate_limited: 'Too many requests. Try again in a moment.',
    data_edit_paused: 'Paused during the upgrade.',
};

/** The line a finished import ends on (the console flow 9 wording). */
export const importFailureLine = (o: Extract<UploadOutcome, { ok: false }>): string => {
    if (o.reason === 'too_large') return 'File too large.';
    if (o.reason === 'cancelled') return '';
    return o.code && Object.prototype.hasOwnProperty.call(UPLOAD_BY_CODE, o.code) ? UPLOAD_BY_CODE[o.code] : 'Upload failed.';
};
